import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';

import CustomImage from '@/components/CustomImage/CustomImage';
import type { ItineraryImportDraft } from '@/models/itineraryImportTypes';
import { summarizeImport } from '@/utils/itineraryImportBuilder';
import {
  formatTripImportTripDateSubtitle,
  heroImageFromDraft,
} from '@/utils/tripImportReviewPlaces';

export interface TripImportSuccessStepProps {
  draft: ItineraryImportDraft | null;
  savedTripId: string | null;
  savedTripTitle: string;
  selectedClientIds: Set<string>;
  isPublishing: boolean;
  /** Creators/advisors see earnings copy under the title. */
  showPublishEarningsCopy?: boolean;
  onClose: () => void;
  onKeepEditing: () => void;
  onPublishAndShare: () => void;
}

export function TripImportSuccessStep({
  draft,
  savedTripId,
  savedTripTitle,
  selectedClientIds,
  isPublishing,
  showPublishEarningsCopy = false,
  onClose,
  onKeepEditing,
  onPublishAndShare,
}: TripImportSuccessStepProps) {
  if (!savedTripId) return null;

  const hero = draft ? heroImageFromDraft(draft) : { primary: null, fallbacks: [] };
  const stats = draft
    ? summarizeImport(draft, selectedClientIds)
    : {
        dayCount: 0,
        placeCount: selectedClientIds.size,
        bookableCount: 0,
      };
  const tripDateSubtitle = draft ? formatTripImportTripDateSubtitle(draft) : null;
  const dayCount = draft?.day_count ?? stats.dayCount;

  return (
    <div className="trip-import-success-step relative flex w-full flex-col overflow-hidden">
      <button
        type="button"
        className="absolute right-4 top-4 flex size-7 items-center justify-center rounded-[14px] bg-[#f2f2f2] text-[#525257] hover:bg-[#e8e8e8] disabled:opacity-40"
        aria-label="Close"
        disabled={isPublishing}
        onClick={onClose}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>

      <header className="flex shrink-0 flex-col items-center gap-3 px-10 pb-0 pt-10 text-center">
        <span
          className="inline-flex size-14 items-center justify-center rounded-[28px] bg-[#e3f0ad] text-[#252528] shadow-[0_0_10px_rgba(0,0,0,0.15)]"
          aria-hidden
        >
          <CheckIcon sx={{ fontSize: 24 }} />
        </span>
        <h2 className="font-lora text-[28px] font-semibold leading-normal tracking-[0.3px] text-[#252528]">
          Your draft trip is ready
        </h2>
        <p className="max-w-[540px] font-manrope text-[14px] font-normal leading-5 tracking-[0.28px] text-[#525257]">
          Publish it to get a shareable link.
          {showPublishEarningsCopy ? ' You earn on every booking.' : null}
        </p>
      </header>

      <div className="shrink-0 px-10 pt-6">
        <div className="flex gap-4 rounded-2xl border border-[#e1e1e7] p-4">
          <div className="relative h-[120px] w-[160px] shrink-0 overflow-hidden rounded-xl bg-[#f3f3f3]">
            <CustomImage
              src={hero.primary ?? undefined}
              fallbackSources={hero.fallbacks}
              alt=""
              fill
              sizes="160px"
              fallbackSrc="/images/default_post.png"
              style={{ objectFit: 'cover', objectPosition: 'center' }}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex rounded bg-[#e1e1e7] px-2 py-0.5 font-manrope text-[11px] font-bold uppercase tracking-[0.22px] text-[#525257]">
                Private
              </span>
              {tripDateSubtitle ? (
                <span className="font-manrope text-[12px] font-normal leading-4 text-[#525257]">
                  {tripDateSubtitle}
                </span>
              ) : null}
            </div>
            <p className="truncate font-manrope text-[20px] font-bold leading-6 tracking-[0.3px] text-[#252528]">
              {savedTripTitle}
            </p>
            <div className="flex flex-wrap items-center gap-4 font-manrope text-[14px] font-normal leading-5 tracking-[0.28px] text-[#525257]">
              <span>
                {dayCount} {dayCount === 1 ? 'day' : 'days'}
              </span>
              <span>
                {stats.placeCount} {stats.placeCount === 1 ? 'place' : 'places'}
              </span>
              <span className="font-semibold text-[#ff5212]">
                {stats.bookableCount} bookable
              </span>
            </div>
          </div>
        </div>
      </div>

      <footer className="flex shrink-0 flex-col gap-4 px-10 pb-9 pt-7 min-[480px]:flex-row min-[480px]:items-center">
        <button
          type="button"
          className="inline-flex h-[52px] flex-1 items-center justify-center rounded-[26px] border border-[#e1e1e7] bg-white px-8 font-manrope text-base font-semibold leading-[22px] tracking-[0.32px] text-[#252528] hover:bg-gray-50 disabled:opacity-40"
          disabled={isPublishing}
          onClick={onKeepEditing}
        >
          Keep editing
        </button>
        <button
          type="button"
          disabled={isPublishing}
          className="inline-flex h-[52px] flex-1 items-center justify-center gap-2.5 rounded-[26px] bg-[#ff5212] px-8 font-manrope text-base font-semibold leading-[22px] tracking-[0.32px] text-white shadow-[0_0_10px_rgba(0,0,0,0.15)] disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onPublishAndShare}
        >
          <IosShareIcon sx={{ fontSize: 20 }} aria-hidden />
          {isPublishing ? 'Publishing…' : 'Publish & share'}
        </button>
      </footer>
    </div>
  );
}
